import { useEffect, useRef, useState, Dispatch, SetStateAction, Fragment } from 'react'
import { useTranslation } from 'next-i18next'

import { ExternalLink } from '~/components/ExternalLink'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ClipboardCopyIcon from '~/components/Icons/svgs/ClipboardCopyIcon'
import VideoIcon from '~/components/Icons/svgs/VideoIcon'
import LocationPinThinIcon from '~/components/Icons/svgs/LocationPinThinIcon'

type SessionInfoLineType = 'link' | 'location'

export default function SessionInfoLine({
  text,
  type,
}: {
  text?: string
  type: SessionInfoLineType
}): React.ReactElement | null {
  const [didCopy, setDidCopy] = useState(false)
  const { t } = useTranslation('generics')

  if (!text) {
    return null
  }

  const handleOnClick = (): void => {
    navigator.clipboard.writeText(text)
    setDidCopy(true)
  }

  return (
    <div className="px-2 py-1 flex items-center border border-gray-light rounded-small text-2.5">
      <span className="shrink-0">{getSessionInfoLineIcon(type)}</span>
      {getSessionInfoLineTag(text, type)}
      <div className="relative shrink-0 ml-auto">
        {didCopy && <CopiedToClipboardMessage didCopy={didCopy} setDidCopy={setDidCopy} />}
        <button
          aria-label={t('ariaLabelButtons.copyToClipboard')}
          className="focus:outline-none"
          onClick={handleOnClick}
        >
          <SVGIcon icon={ClipboardCopyIcon} pathClassName="text-black" size={14} />
        </button>
      </div>
    </div>
  )
}

function CopiedToClipboardMessage({
  didCopy,
  setDidCopy,
}: {
  didCopy: boolean
  setDidCopy: Dispatch<SetStateAction<boolean>>
}): React.ReactElement {
  const { t } = useTranslation('generics')
  const messageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (didCopy && messageRef.current) {
      const message = messageRef.current
      message.style.transition = '200ms opacity ease'
      message.style.opacity = '100%'

      setTimeout(() => {
        message.style.opacity = '0'
      }, 2000)

      message.ontransitionend = () => {
        setDidCopy(false)
      }
    }
  }, [didCopy, setDidCopy])

  return (
    <div className="absolute -top-2 left-1/2 transform -translate-y-full -translate-x-1/2" ref={messageRef}>
      <span className="p-1 bg-purple-dark text-white rounded-small">{t('copied')}!</span>
    </div>
  )
}

function getSessionInfoLineIcon(type: SessionInfoLineType): React.ReactElement {
  const ICON_SIZE = 10

  switch (type) {
    case 'link':
      return <SVGIcon icon={VideoIcon} pathClassName="text-black" size={ICON_SIZE} />

    case 'location':
      return <SVGIcon icon={LocationPinThinIcon} pathClassName="text-black" size={ICON_SIZE} />

    default:
      return <Fragment />
  }
}

function getSessionInfoLineTag(text: string, type: SessionInfoLineType): React.ReactElement {
  const sharedClassNames = 'mx-2 truncate'

  switch (type) {
    case 'link':
      return (
        <ExternalLink className={`${sharedClassNames} underline text-purple text-ellipsis	`} href={text}>
          {text}
        </ExternalLink>
      )

    case 'location':
      return <span className={`${sharedClassNames}`}>{text}</span>

    default:
      return <Fragment />
  }
}
